import React, { FC } from 'react';

import { GrafanaTheme2 } from '@grafana/data';
import { Icon, useStyles2 } from '@grafana/ui';

import { css, cx } from '@emotion/css';

import { Analysis, AnalysisDisplayStatus } from 'types';
import { getAnalysisDisplayTitle } from 'utils/utils.sift';

import AnalysisIcon from './AnalysisIcon';

// Component Props Interface
interface Props {
  analyses: Analysis[];
  selectedAnalysisId?: string;
  onSelection: (analysisId: string) => void;
}

// Declare Component
export const List: FC<Props> = ({ analyses, selectedAnalysisId, onSelection }) => {
  const styles = useStyles2(getStyles);
  const applicationList = analyses.filter((item) => item.displayCategory === 'application') || [];
  const infrastructureList = analyses.filter((item) => item.displayCategory === 'infrastructure') || [];
  const otherList = analyses.filter((item) => item.displayCategory === 'other') || [];
  const customList = analyses.filter((item) => item.displayCategory === 'custom') || [];
  const internalList = analyses.filter((item) => item.displayCategory === 'internal') || [];

  const isAnalysisValid = analyses.find((analysis) => analysis.id === selectedAnalysisId) !== undefined;

  return (
    <div>
      <div className={cx(styles.home, isAnalysisValid ? {} : styles.selected)} onClick={() => onSelection('')}>
        <Icon name="home-alt" size="xl" /> Home
      </div>

      {applicationList.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Application</div>
          {applicationList.map((analysis) => (
            <AnalysisListItem
              key={analysis.id}
              selected={analysis.id === selectedAnalysisId}
              displayStatus={analysis.displayStatus}
              name={analysis.name}
              message={analysis.result.message}
              onClick={() => onSelection(analysis.id)}
            />
          ))}
        </div>
      ) : null}

      {infrastructureList.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Infrastructure</div>
          {infrastructureList.map((analysis) => (
            <AnalysisListItem
              key={analysis.id}
              selected={analysis.id === selectedAnalysisId}
              displayStatus={analysis.displayStatus}
              name={analysis.name}
              message={analysis.result.message}
              onClick={() => onSelection(analysis.id)}
            />
          ))}
        </div>
      ) : null}

      {otherList.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Other</div>
          {otherList.map((analysis) => (
            <AnalysisListItem
              key={analysis.id}
              selected={analysis.id === selectedAnalysisId}
              displayStatus={analysis.displayStatus}
              name={analysis.name}
              message={analysis?.result?.message ?? ''}
              onClick={() => onSelection(analysis.id)}
            />
          ))}
        </div>
      ) : null}

      {customList.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Custom</div>
          {customList.map((analysis) => (
            <AnalysisListItem
              key={analysis.id}
              selected={analysis.id === selectedAnalysisId}
              displayStatus={analysis.displayStatus}
              name={analysis.name}
              message={analysis.result.message}
              onClick={() => onSelection(analysis.id)}
            />
          ))}
        </div>
      ) : null}

      {internalList.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Internal Testing</div>
          {internalList.map((analysis) => (
            <AnalysisListItem
              key={analysis.id}
              selected={analysis.id === selectedAnalysisId}
              displayStatus={analysis.displayStatus}
              name={analysis.name}
              message={analysis.result.message}
              onClick={() => onSelection(analysis.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

interface AnalysisListItemProps {
  selected: boolean;
  displayStatus: AnalysisDisplayStatus; // 'finished' | 'pending' | 'running' | 'skipped'
  name: string;
  message: string;
  onClick: () => void;
}

export const AnalysisListItem: FC<AnalysisListItemProps> = ({ selected, displayStatus, name, message, onClick }) => {
  const styles = useStyles2(getStyles);

  return (
    <>
      <div
        className={cx(
          styles.analysisLink,
          displayStatus === 'interesting' ? styles.highlight : {},
          selected ? styles.selected : {}
        )}
        onClick={onClick}
      >
        <AnalysisIcon displayStatus={displayStatus} />
        {getAnalysisDisplayTitle(name)}
      </div>
      {displayStatus === 'interesting' ? (
        <div className={styles.interestingMessage} onClick={onClick}>
          {message}
        </div>
      ) : null}
    </>
  );
};

// Define CSS Styles
const getStyles = (theme: GrafanaTheme2) => {
  return {
    home: css`
      display: flex;
      gap: 6px;
      align-items: center;
      padding: 2px 2px 2px 8px;
      border-left: 4px solid rgba(255, 255, 255, 0);
      cursor: pointer;
    `,
    selected: css`
      border-left: 4px solid rgb(255, 120, 10);
      background-color: ${theme.colors.background.secondary};
      cursor: default;
    `,
    section: css`
      display: flex;
      flex-direction: column;
      margin-top: 8px;
    `,
    sectionTitle: css`
      padding: 8px 0px;
      font-size: 11px;
      color: ${theme.colors.text.secondary};
    `,
    interestingMessage: css`
      padding: 0px 0px 6px 42px;
      font-size: 11px;
      cursor: pointer;
    `,
    analysisLink: css`
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 4px 0px 4px 12px;
      color: ${theme.colors.text.secondary};
      border-left: 4px solid rgba(255, 255, 255, 0);
      border-radius: 4px 0px 0px 4px;
      cursor: pointer;
    `,
    highlight: css`
      color: ${theme.colors.text.primary};
    `,
    loading: css`
      color: rgb(50, 116, 217);
    `,
    skipped: css`
      color: ${theme.colors.text.maxContrast};
    `,
    success: css`
      color: rgb(86, 166, 75);
    `,
    interesting: css`
      color: rgb(255, 120, 10);
    `,
    failure: css`
      color: rgb(224, 47, 68);
    `,
  };
};

// Export Component
export default List;
